import { Attachment, IconButton, notify, TABLE_CELL_NOT_CLICKABLE_CLASS, useModal } from '@billy/design-system'
import { Tooltip } from '@shinetools/lumen-react'
import { ReactElement, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ChooseFileDropdown } from '@features/attachments/elements/ChooseFileDropdown'
import { useUpdateAttachment } from '@features/attachments/hooks/useUpdateAttachment'
import { useInvalidateBills } from '@features/bills/hooks/useInvalidateBills'
import { useUploadBillAttachments } from '@features/bills/hooks/useUploadBillAttachments'
import { useInvalidateDaybookTransaction } from '@features/daybook/hooks/useInvalidateDaybookTransaction'
import { useUploadDaybookTransactionAttachments } from '@features/daybook/hooks/useUploadDaybookTransactionAttachments'
import { TransactionOriginatorType } from '@features/transactions/enums/transactionOriginatorType'
import { useInvalidateTransactions } from '@features/transactions/hooks/useInvalidateTransactions'

import { ModalId } from '../../../../../../enums/modalId'
import { NotificationKeys } from '../../../../../../enums/notificationKeys'
import { useSegment } from '../../../../../../hooks'
import { AttachmentPreviewTarget } from '../../types/attachmentPreviewTarget'
import * as Styled from '../TransactionsTable/styles'

interface TransactionsTableAttachmentCellProps {
  hasAttachment?: boolean | null
  loadingTransactionId?: string
  openAttachmentPreview: (target: AttachmentPreviewTarget) => void
  originatorId: string
  originatorType?: TransactionOriginatorType
  transactionId: string
  voided?: boolean
}

export const TransactionsTableAttachmentCell = ({
  hasAttachment,
  loadingTransactionId,
  openAttachmentPreview,
  originatorId,
  originatorType,
  transactionId,
  voided,
}: TransactionsTableAttachmentCellProps): ReactElement | null => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const [isAddDropdownOpen, setIsAddDropdownOpen] = useState(false)
  const [isAddTooltipOpen, setIsAddTooltipOpen] = useState(false)

  const modalId = `${ModalId.AttachmentsModal}-${transactionId}`
  const { isOpen: isUploadsModalOpen } = useModal(modalId)

  const handleAddTooltipOpenChange = useCallback(
    (open: boolean) => {
      if (open && (isAddDropdownOpen || isUploadsModalOpen)) {
        return
      }

      setIsAddTooltipOpen(open)
    },
    [isAddDropdownOpen, isUploadsModalOpen],
  )

  useEffect(() => {
    if (isAddDropdownOpen || isUploadsModalOpen) {
      setIsAddTooltipOpen(false)
    }
  }, [isAddDropdownOpen, isUploadsModalOpen])

  const isBill = originatorType === TransactionOriginatorType.Bill
  const isDaybookTransaction = originatorType === TransactionOriginatorType.DaybookTransaction

  const canAddAttachment = !voided && (isBill || isDaybookTransaction)

  const { invalidateBill } = useInvalidateBills()
  const { invalidateDaybookTransaction } = useInvalidateDaybookTransaction()
  const { invalidateTransactions } = useInvalidateTransactions()

  const { handleFilesDropped: handleBillFilesDropped, isUploading: isBillUploading } = useUploadBillAttachments({
    billId: isBill ? originatorId : undefined,
  })
  const { handleFilesDropped: handleDaybookFilesDropped, isUploading: isDaybookUploading } =
    useUploadDaybookTransactionAttachments({
      daybookTransactionId: isDaybookTransaction ? originatorId : undefined,
    })

  const { update: updateAttachment, isLoading: isUpdatingAttachment } = useUpdateAttachment({
    onSuccess: () => {
      if (isBill) {
        invalidateBill(originatorId)
        notify({
          id: NotificationKeys.BillAttachmentFilesUpdate,
          message: t('bill.notification.attachments_update_success'),
          variant: 'success',
        })
      } else {
        invalidateDaybookTransaction(originatorId)
        notify({
          id: NotificationKeys.DaybookTransactionAttachmentFilesUpdate,
          message: t('journal.notification.attachment_upload_success'),
          variant: 'success',
        })
      }
      invalidateTransactions()
    },
  })

  const uploadFilesDropped = isBill ? handleBillFilesDropped : handleDaybookFilesDropped
  const isUploading = isBillUploading || isDaybookUploading || isUpdatingAttachment

  const handleAttachmentClick = useCallback(() => {
    if (!originatorType) {
      return
    }

    track('xxx - Bookkeeping - Ledger', {
      action: 'Preview attachment',
      transactionId,
      transactionType: originatorType,
    })

    openAttachmentPreview({ originatorId, originatorType, transactionId })
  }, [openAttachmentPreview, originatorId, originatorType, track, transactionId])

  const trackAddAttachment = useCallback(() => {
    track('xxx - Bookkeeping - Ledger', { action: 'Add attachment', transactionId, transactionType: originatorType })
  }, [originatorType, track, transactionId])

  const handleFilesDropped = useCallback(
    (files: File[]) => {
      trackAddAttachment()
      uploadFilesDropped(files)
    },
    [trackAddAttachment, uploadFilesDropped],
  )

  const handleAttachmentSelect = useCallback(
    (attachment: Attachment) => {
      trackAddAttachment()
      updateAttachment({
        id: attachment.id,
        ownerReference: isBill ? `bill:${originatorId}` : `daybookTransaction:${originatorId}`,
        ownerId: originatorId,
      })
    },
    [isBill, originatorId, trackAddAttachment, updateAttachment],
  )

  if (hasAttachment) {
    return (
      <Tooltip label={t('transactions.table.attachment.show_tooltip')} placement="top">
        <Styled.TableCellButtonWrapper>
          <IconButton
            icon="paperclip"
            loading={loadingTransactionId === transactionId}
            onClick={handleAttachmentClick}
            size="sm"
          />
        </Styled.TableCellButtonWrapper>
      </Tooltip>
    )
  }

  if (!canAddAttachment) {
    return null
  }

  return (
    <Tooltip
      label={t('transactions.table.attachment.add_tooltip')}
      onOpenChange={handleAddTooltipOpenChange}
      open={isAddTooltipOpen}
      openDelay={300}
      placement="top"
    >
      <Styled.TableCellButtonWrapper>
        <ChooseFileDropdown
          dropdownClassName={TABLE_CELL_NOT_CLICKABLE_CLASS}
          modalId={modalId}
          onClose={() => setIsAddDropdownOpen(false)}
          onFileFromDeviceSelect={handleFilesDropped}
          onFileFromUploadsSelect={handleAttachmentSelect}
          onOpen={() => setIsAddDropdownOpen(true)}
          trigger={<IconButton icon="plus" size="sm" loading={isUploading} />}
        />
      </Styled.TableCellButtonWrapper>
    </Tooltip>
  )
}
